import { baseApi } from '@/shared/api'
import type {
  AddManagerCartItemBody,
  CommandState,
  CreateManagerCartBody,
  ManagerCart,
  ManagerCartAppend,
  ManagerCartAppendBody,
  ManagerCartCheckout,
  ManagerCartCheckoutBody,
  ManagerCartList,
  ManagerCartReview,
  ManagerCartsQuery,
  UpdateManagerCartBody,
  UpdateManagerCartItemsBody,
} from '../model/types'

/** Команда не закрыта — опрашиваем; ответ 1С по резерву может ждать сутки. */
const COMMAND_POLL_MS = 10_000

const cartTags = (id: string) => [
  { type: 'ManagerCart' as const, id },
  { type: 'ManagerCart' as const, id: 'LIST' },
]

export const managerCartApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getManagerCarts: build.query<ManagerCartList, ManagerCartsQuery>({
      query: (params) => ({ url: '/carts', params }),
      providesTags: (res) => [
        { type: 'ManagerCart', id: 'LIST' },
        ...(res?.items ?? []).map((item) => ({ type: 'ManagerCart' as const, id: item.id })),
      ],
    }),

    getManagerCart: build.query<ManagerCart, string>({
      query: (id) => ({ url: `/carts/${encodeURIComponent(id)}` }),
      providesTags: (_res, _err, id) => [{ type: 'ManagerCart', id }],
    }),

    /** Контекст корзины — клиент, учётная запись, юрлицо, расчёт — задаётся один раз здесь. */
    createManagerCart: build.mutation<ManagerCart, CreateManagerCartBody>({
      query: (body) => ({ url: '/carts', method: 'POST', body }),
      invalidatesTags: [{ type: 'ManagerCart', id: 'LIST' }],
    }),

    updateManagerCart: build.mutation<ManagerCart, { id: string } & UpdateManagerCartBody>({
      query: ({ id, ...body }) => ({
        url: `/carts/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_res, _err, { id }) => cartTags(id),
    }),

    discardManagerCart: build.mutation<void, string>({
      query: (id) => ({ url: `/carts/${encodeURIComponent(id)}`, method: 'DELETE' }),
      invalidatesTags: (_res, _err, id) => cartTags(id),
    }),

    copyManagerCart: build.mutation<ManagerCart, { id: string; title?: string }>({
      query: ({ id, ...body }) => ({
        url: `/carts/${encodeURIComponent(id)}/copy`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'ManagerCart', id: 'LIST' }],
    }),

    addManagerCartItem: build.mutation<ManagerCart, { id: string } & AddManagerCartItemBody>({
      query: ({ id, ...body }) => ({
        url: `/carts/${encodeURIComponent(id)}/items`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_res, _err, { id }) => cartTags(id),
    }),

    /** Количество строк; ноль удаляет строку. Ответ — корзина целиком. */
    updateManagerCartItems: build.mutation<
      ManagerCart,
      { id: string } & UpdateManagerCartItemsBody
    >({
      query: ({ id, ...body }) => ({
        url: `/carts/${encodeURIComponent(id)}/items`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_res, _err, { id }) => cartTags(id),
    }),

    removeManagerCartItems: build.mutation<ManagerCart, { id: string; ids: string[] }>({
      query: ({ id, ids }) => ({
        url: `/carts/${encodeURIComponent(id)}/items`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_res, _err, { id }) => cartTags(id),
    }),

    /** Сверка с каталогом: цена, остаток, пропажа. Предупреждение, а не блокировка. */
    getManagerCartReview: build.query<ManagerCartReview, string>({
      query: (id) => ({ url: `/carts/${encodeURIComponent(id)}/review` }),
      providesTags: (_res, _err, id) => [{ type: 'ManagerCart', id: `${id}:review` }],
    }),

    /**
     * Оформить резерв от имени клиента. `Idempotency-Key` обязателен: повтор того же нажатия
     * не создаст второй заказ. Ключ — на попытку, его заводит вызывающий.
     */
    checkoutManagerCart: build.mutation<
      ManagerCartCheckout,
      { id: string; idempotencyKey: string } & ManagerCartCheckoutBody
    >({
      query: ({ id, idempotencyKey, ...body }) => ({
        url: `/carts/${encodeURIComponent(id)}/checkout`,
        method: 'POST',
        body,
        headers: { 'Idempotency-Key': idempotencyKey },
      }),
      invalidatesTags: (_res, _err, { id }) => cartTags(id),
    }),

    /** Дозаказать корзину в существующий счёт клиента; ключ идемпотентности — на попытку. */
    appendManagerCart: build.mutation<
      ManagerCartAppend,
      { id: string; invoiceNo: string; idempotencyKey: string } & ManagerCartAppendBody
    >({
      query: ({ id, invoiceNo, idempotencyKey, ...body }) => ({
        url: `/carts/${encodeURIComponent(id)}/append/${encodeURIComponent(invoiceNo)}`,
        method: 'POST',
        body,
        headers: { 'Idempotency-Key': idempotencyKey },
      }),
      invalidatesTags: (_res, _err, { id }) => [
        ...cartTags(id),
        { type: 'Order', id: 'LINES' },
        { type: 'Order', id: 'INVOICES' },
      ],
    }),

    /** Состояние команды обмена с 1С; читает только тот, кто её завёл. */
    getCommandState: build.query<CommandState, string>({
      query: (id) => ({ url: `/commands/${encodeURIComponent(id)}` }),
      providesTags: (_res, _err, id) => [{ type: 'Command', id }],
    }),
  }),
})

export const {
  useGetManagerCartsQuery,
  useGetManagerCartQuery,
  useCreateManagerCartMutation,
  useUpdateManagerCartMutation,
  useDiscardManagerCartMutation,
  useCopyManagerCartMutation,
  useAddManagerCartItemMutation,
  useUpdateManagerCartItemsMutation,
  useRemoveManagerCartItemsMutation,
  useGetManagerCartReviewQuery,
  useCheckoutManagerCartMutation,
  useAppendManagerCartMutation,
  useGetCommandStateQuery,
} = managerCartApi

export { COMMAND_POLL_MS }
